import { StandardEvents } from '@shopify/events';

/*
 * A spinner on the controls that add to the cart.
 *
 * Nico built the mechanism, 0b53a9f, and asked on 2026-09-02 for it wherever a
 * control waits: "es wäre cool, dass wir bei User-Feedback, der irgendwie ein
 * bisschen dauert, weil im Hintergrund ein AJAX-Request läuft, dass wir das auch
 * einbauen könnten, um den Nutzer nicht rätselnd zurückzulassen."
 *
 * The busy state is `aria-busy` on the button, which is what `pp-button` already
 * dresses. Nothing about the add itself is touched: Horizon still owns the
 * request, the errors and the drawer.
 */

const BUTTON = '[ref="addToCartButton"]';
const BUSY = 'aria-busy';

const SAFETY_MS = 6000;

/*
 * The floor the mark stays up for once it is set.
 *
 * Two adds in a row are not the same length. The first has to fetch and build
 * the cart drawer; every one after it reuses what is already there and settles
 * in a fraction of the time. Without a floor the spinner on a repeat add is on
 * screen for long enough to satisfy a stopwatch and not long enough to be seen,
 * which is exactly what Oksana reported on 4 September: turning the first time,
 * never again.
 *
 * 450 is the usual floor for this: long enough to register as a state the
 * control was in, short enough that it is not felt as a delay. Nothing waits on
 * it. The add has already finished; this only holds the picture.
 */
const MIN_VISIBLE_MS = 450;

/*
 * The release signal is the cart icon changing, not the cart event.
 *
 * `shopify:cart:lines-update` looked like the obvious choice and is not: it is
 * dispatched by Shopify's own platform script rather than by the theme, and it
 * was measured arriving before the submit handler had marked anything, leaving
 * the mark with nothing left to clear it. It is still listened for below,
 * because when it does arrive late it is the earliest signal there is, but it
 * cannot be the only one.
 *
 * The icon is the theme's own and it is redrawn from the add's response, so it
 * changes if and only if the cart did.
 *
 * Watched rather than polled. `requestAnimationFrame` was the first attempt and
 * is wrong here: a browser stops serving frames to a tab nobody is looking at,
 * so a shopper who switched away mid add would come back to a button still
 * turning. A MutationObserver fires either way.
 */
const observers = new Set();

const stopWatching = () => {
  for (const observer of observers) observer.disconnect();
  observers.clear();
};

/*
 * When the mark may come down, and whether a release is already waiting for
 * that moment. Module scope rather than per submit, because only one add is
 * ever in flight: the second press during a wait is turned away below.
 */
let releaseAt = 0;
let scheduled = false;

const releaseNow = () => {
  stopWatching();
  scheduled = false;
  releaseAt = 0;

  for (const button of document.querySelectorAll(`${BUTTON}[${BUSY}]`)) {
    button.removeAttribute(BUSY);
  }
};

const release = () => {
  const remaining = releaseAt - performance.now();

  if (remaining <= 0) {
    releaseNow();
    return;
  }

  // The signals overlap by design — the icon, the cart event, the backstop —
  // so the first one to arrive owns the wait and the rest are no ops.
  if (scheduled) return;
  scheduled = true;
  setTimeout(releaseNow, remaining);
};

/*
 * Keep the mark on whatever button is on the page, not on the node it was put
 * on.
 *
 * Horizon re-renders the add to cart control after an add: the node carries
 * `data-added` afterwards, and it is a different node from the one that was
 * clicked. On the next add it is re-rendered again, early, and a mark placed
 * before that arrives on a node that is thrown away moments later. The
 * replacement is drawn from the server's markup, which has no `aria-busy`, so
 * the spinner vanishes with the node that carried it.
 *
 * Watching for the button rather than remembering one means the mark follows
 * the re-render instead of dying with it. It runs only while a mark is owed,
 * and `stopWatching` takes it down with everything else.
 *
 * Watched from the form rather than from `add-to-cart-component`. Which level
 * gets replaced was never pinned down: on the measured path nothing was
 * replaced at all, and the one replacement that was seen came after a drawer
 * had been opened and closed. An observer on a node that is itself swapped out
 * is a detached observer that never fires again, and the form is the outermost
 * node this can be sure of, because the submit that brought us here came from
 * it. Nothing is lost by watching from higher up: the query below is anchored
 * on the component, so a page with several add to cart forms still marks only
 * the one that was submitted.
 *
 * @param {HTMLFormElement} form
 */
const watchButton = (form) => {
  const observer = new MutationObserver(() => {
    if (releaseAt === 0) return;

    const container = form.querySelector('add-to-cart-component');
    if (!container) return;

    const button = container.querySelector(BUTTON);
    if (button instanceof HTMLElement && !button.hasAttribute(BUSY)) {
      button.setAttribute(BUSY, 'true');
    }
  });

  observer.observe(form, { subtree: true, childList: true });
  observers.add(observer);
};

const watchCartIcon = () => {
  const bubble = document.querySelector('[ref="cartBubble"]');
  if (!bubble) return;

  const observer = new MutationObserver(release);
  observer.observe(bubble, { subtree: true, childList: true, characterData: true });
  observers.add(observer);
};

/**
 * On `document` and on the capture phase, and both halves were arrived at by
 * elimination.
 *
 * Not the bubble phase: Horizon's own handler is a capture listener on
 * `document`, and by the time a submit finished bubbling back here the add had
 * already run.
 *
 * Not `window` either. The subscription popup holds an add back from a capture
 * listener on `window` and calls `stopPropagation`; a listener on the same node
 * still runs, so a swallowed add marked a button with nothing behind it and it
 * span in front of a customer who had not answered the question yet.
 * `document` capture sits between the two: the popup's `stopPropagation` stops
 * the event before it arrives, and Horizon's handler, which runs first, only
 * starts a request that takes the better part of a second.
 *
 * @param {SubmitEvent} event
 */
const onSubmit = (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;

  // Horizon's own marker, so this follows the platform rather than a list of
  // our class names that would go stale the next time one is renamed.
  const container = form.querySelector('add-to-cart-component');
  if (!container) return;

  const button = container.querySelector(BUTTON);
  if (!(button instanceof HTMLElement)) return;
  if (button.hasAttribute(BUSY)) return;

  button.setAttribute(BUSY, 'true');
  releaseAt = performance.now() + MIN_VISIBLE_MS;
  scheduled = false;
  watchButton(form);
  watchCartIcon();

  // A failed add reports nothing at all on some paths, and a button left
  // turning reads as an order still in flight. Six seconds is far past a
  // healthy add here, which measures around 800ms.
  setTimeout(release, SAFETY_MS);
};

document.addEventListener('submit', onSubmit, true);
document.addEventListener(StandardEvents.cartLinesUpdate, release);
document.addEventListener(StandardEvents.cartError, release);
